/**
 * MonthlyCalendar.js
 * Visualização mensal estilo Google Calendar.
 * Cada célula de dia mostra mini-badges coloridos com contagem por status.
 * Clicar em um dia navega para a view diária daquele dia.
 */

import React, { useMemo } from 'react';
import styles from './MonthlyCalendar.module.css';
import AppointmentsState from '../../../consts/AppointmentsState';
import { getStatusColors } from '../../../consts/AppointmentStatusColors';

// ─── Nomes dos dias da semana (Dom → Sáb) ─────────────────────────────────────
const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// Rótulos curtos para os badges
const STATUS_SHORT_LABELS = {
  [AppointmentsState.Scheduled]:         'Agendado',
  [AppointmentsState.Rescheduled]:       'Reagendado',
  [AppointmentsState.Confirmed]:         'Confirmado',
  [AppointmentsState.ConfirmedByPatient]:'Conf. paciente',
  [AppointmentsState.Waiting]:           'Aguardando',
  [AppointmentsState.Doing]:             'Em atend.',
  [AppointmentsState.Done]:              'Realizado',
  [AppointmentsState.Canceled]:          'Cancelado',
  [AppointmentsState.CanceledByPatient]: 'Canc. paciente',
  [AppointmentsState.Missed]:            'Falta',
  [AppointmentsState.Blocked]:           'Bloqueado',
};

// Ordem de exibição dos badges — prioridade de negócio:
// Realizados → Agendados → Confirmações → Em atend. → Aguardando → Falta → Cancelados → Bloqueados → Reagendados
const STATUS_DISPLAY_ORDER = [
  AppointmentsState.Done,              // Realizado
  AppointmentsState.Scheduled,         // Agendado
  AppointmentsState.Confirmed,         // Confirmado
  AppointmentsState.ConfirmedByPatient,// Confirmado pelo paciente
  AppointmentsState.Doing,             // Em atendimento
  AppointmentsState.Waiting,           // Aguardando no local
  AppointmentsState.Missed,            // Falta
  AppointmentsState.Canceled,          // Cancelado
  AppointmentsState.CanceledByPatient, // Cancelado pelo paciente
  AppointmentsState.Blocked,           // Bloqueado
  AppointmentsState.Rescheduled,       // Reagendado
];

// ─── Formata chave de data 'YYYY-MM-DD' a partir de um objeto Date ou string ──
function toDateKey(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ─── Conta agendamentos por status para um array de appointments ──────────────
function countByStatus(dayAppointments) {
  const counts = {};
  dayAppointments.forEach(apt => {
    // Ignora slots disponíveis
    if (apt.status === AppointmentsState.Available) return;
    const status = apt.status || 'default';
    counts[status] = (counts[status] || 0) + 1;
  });
  return counts;
}

// ─── Componente de célula de dia ──────────────────────────────────────────────
const DayCell = ({ date, isCurrentMonth, isToday, appointments, onDayClick }) => {
  const counts = useMemo(() => countByStatus(appointments), [appointments]);

  // Ordena os status para exibição — mostra TODOS (sem limite)
  const badgeEntries = STATUS_DISPLAY_ORDER
    .filter(status => counts[status] > 0)
    .map(status => {
      // Cores da fonte central (já com as escolhidas pela empresa)
      const colors = getStatusColors(status);
      return {
        status,
        count: counts[status],
        bg:     colors.bg,
        border: colors.borderColor,
        text:   colors.textColor,
        label:  STATUS_SHORT_LABELS[status] || status,
      };
    });

  const cellClass = [
    styles.dayCell,
    !isCurrentMonth ? styles.dayCellOutside : '',
  ].filter(Boolean).join(' ');

  const numberClass = [
    styles.dayNumber,
    isToday ? styles.dayNumberToday : '',
  ].filter(Boolean).join(' ');

  function handleClick() {
    if (onDayClick) onDayClick(date);
  }

  return (
    <div
      className={cellClass}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
    >
      <span className={numberClass}>{date.getDate()}</span>

      {badgeEntries.length > 0 && (
        <div className={styles.badgeList}>
          {badgeEntries.map(({ status, count, bg, border, text, label }) => (
            <div
              key={status}
              className={styles.badge}
              style={{
                backgroundColor: bg,
                borderLeft: `3px solid ${border}`,
                color: text,
              }}
            >
              <span className={styles.badgeCount} style={{ color: text }}>{count}</span>
              <span className={styles.badgeLabel} style={{ color: text }}>{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────
/**
 * @param {Object}   props
 * @param {Array}    props.appointments  - Array de todos os agendamentos do mês
 * @param {Date}     props.currentDate   - Date object do mês atual (qualquer dia do mês)
 * @param {Function} props.onDayClick    - Callback(date) chamado ao clicar em um dia
 */
const MonthlyCalendar = ({ appointments = [], currentDate, onDayClick }) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // ── Calcula os dias da grade mensal ──────────────────────────────────────────
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Primeiro e último dia do mês
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Dia da semana do primeiro dia (0=Dom, 6=Sáb)
    const startDow = firstDay.getDay();
    // Dia da semana do último dia
    const endDow = lastDay.getDay();

    const days = [];

    // Dias do mês anterior para preencher o início da grade
    for (let i = startDow - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }

    // Dias do mês atual
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push({ date: new Date(year, month, d), isCurrentMonth: true });
    }

    // Dias do próximo mês para completar a última linha (até Sáb)
    const remaining = endDow < 6 ? 6 - endDow : 0;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  }, [currentDate]);

  // ── Agrupa agendamentos por chave de data 'YYYY-MM-DD' ───────────────────────
  const appointmentsByDay = useMemo(() => {
    const map = {};
    appointments.forEach(apt => {
      if (!apt.date) return;
      const key = toDateKey(apt.date);
      if (!map[key]) map[key] = [];
      map[key].push(apt);
    });
    return map;
  }, [appointments]);

  return (
    <div className={styles.root}>
      <div className={styles.monthGrid}>
        {/* Cabeçalho dos dias da semana */}
        {DAY_NAMES.map(name => (
          <div key={name} className={styles.dayHeader}>{name}</div>
        ))}

        {/* Células dos dias */}
        {calendarDays.map(({ date, isCurrentMonth }, idx) => {
          const key = toDateKey(date);
          const dayAppointments = appointmentsByDay[key] || [];
          const isToday = date.toDateString() === today.toDateString();

          return (
            <DayCell
              key={`${key}-${idx}`}
              date={date}
              isCurrentMonth={isCurrentMonth}
              isToday={isToday}
              appointments={dayAppointments}
              onDayClick={onDayClick}
            />
          );
        })}
      </div>
    </div>
  );
};

export default MonthlyCalendar;
